import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
    RebelCard,
    RebelSectionTitle,
    RebelButton,
    RebelAccentButton,
    RebelDangerButton,
    RebelColors,
    RebelIcon,
    RebelEmptyState,
    RebelLoadingSpinner,
    RebelModal,
    RebelModalHeader,
    RebelModalBody,
    RebelModalFooter,
    RebelLabel,
    RebelInput,
    RebelTextarea
} from "../../../styles/rebel-experiences";
import { GetDB } from "../../../utils/database/firebase-budget-tool";
import { listenToCollection, setDocument, deleteDocument } from "../../../utils/firestore-functions";

const RebelConfigList = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [devMode, setDevMode] = useState(false);

    // Category modal state
    const [categoryModal, setCategoryModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [categoryForm, setCategoryForm] = useState({
        id: '',
        name: '',
        description: '',
        order: 0,
        _docId: ''  // Firestore document ID (distinct from data 'id' field)
    });

    // Delete category modal state
    const [deleteCategoryModal, setDeleteCategoryModal] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);

    // Confirm edit modal state (for breaking changes)
    const [confirmEditModal, setConfirmEditModal] = useState(false);
    const [pendingEdit, setPendingEdit] = useState(null);

    const budgetDB = GetDB();

    useEffect(() => {
        const unsubscribe = listenToCollection(budgetDB, "agenda-block-types", (data) => {
            if (!data || data.length === 0) {
                console.log("No categories found in 'agenda-block-types' collection. Data received:", data);
                setCategories([]);
            } else {
                console.log("Categories loaded from database:", data);

                // Check for duplicate IDs and warn
                const idCounts = {};
                data.forEach(cat => {
                    idCounts[cat.id] = (idCounts[cat.id] || 0) + 1;
                });
                const duplicates = Object.entries(idCounts).filter(([id, count]) => count > 1);
                if (duplicates.length > 0) {
                    console.warn("WARNING: Duplicate category IDs found:", duplicates);
                    console.warn("Full duplicate data:", data.filter(cat => duplicates.some(([id]) => cat.id === id)));
                }

                // Deduplicate by id (keep the first occurrence)
                const seen = new Set();
                const uniqueCategories = data.filter(cat => {
                    if (seen.has(cat.id)) {
                        console.warn(`Filtering out duplicate category with id: ${cat.id}`);
                        return false;
                    }
                    seen.add(cat.id);
                    return true;
                });

                const sortedCategories = uniqueCategories.sort((a, b) => {
                    if (a.order !== undefined && b.order !== undefined) {
                        return a.order - b.order;
                    }
                    return (a.name || '').localeCompare(b.name || '');
                });
                setCategories(sortedCategories);
            }
            setLoading(false);
        }, (error) => {
            console.error("Error fetching categories from 'agenda-block-types':", error);
            setLoading(false);
        });

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [budgetDB]);

    // Category CRUD functions
    const openAddCategoryModal = () => {
        setCategoryForm({
            id: '',
            name: '',
            description: '',
            order: categories.length + 1,
            _docId: ''
        });
        setEditingCategory(null);
        setCategoryModal(true);
    };

    const openEditCategoryModal = (category) => {
        console.log('=== OPEN EDIT MODAL ===');
        console.log('Category being edited:', category);
        console.log('Category data ID:', category.id);
        console.log('Firestore document ID (_docId):', category._docId);
        setCategoryForm({
            id: category.id,
            name: category.name || '',
            description: category.description || '',
            order: category.order || 0,
            _docId: category._docId  // Store the actual Firestore document ID
        });
        setEditingCategory(category);
        setCategoryModal(true);
    };

    const closeCategoryModal = () => {
        setCategoryModal(false);
        setEditingCategory(null);
        setCategoryForm({ id: '', name: '', description: '', order: 0, _docId: '' });
    };

    const handleCategoryFormChange = (field, value) => {
        setCategoryForm(prev => ({ ...prev, [field]: value }));
    };

    const saveCategory = async () => {
        console.log('=== SAVE CATEGORY ===');
        console.log('editingCategory:', editingCategory);
        console.log('categoryForm:', categoryForm);

        // If editing, show confirmation modal
        if (editingCategory) {
            const pendingData = {
                type: 'category',
                data: categoryForm,
                original: editingCategory
            };
            console.log('Setting pendingEdit to:', pendingData);
            setPendingEdit(pendingData);
            setCategoryModal(false);
            setConfirmEditModal(true);
            return;
        }

        // Adding new category
        await executeCategorySave();
    };

    const executeCategorySave = async () => {
        console.log('=== EXECUTE CATEGORY SAVE ===');
        console.log('pendingEdit at execution:', pendingEdit);
        console.log('categoryForm at execution:', categoryForm);
        console.log('editingCategory at execution:', editingCategory);

        setSaving(true);
        try {
            // Determine if we're editing (use existing Firestore doc ID) or creating (generate new ID)
            // Use _docId (Firestore document ID) for editing, NOT the data 'id' field
            const existingDocId = pendingEdit?.original?._docId || categoryForm._docId;
            const isEditing = !!existingDocId;

            // For the Firestore document ID: use existing _docId if editing, or generate from name for new
            const firestoreDocId = existingDocId || categoryForm.name.toLowerCase().replace(/\s+/g, '-');

            // For the data 'id' field: keep consistent with document ID
            const dataId = existingDocId || categoryForm.name.toLowerCase().replace(/\s+/g, '-');

            console.log('isEditing:', isEditing);
            console.log('existingDocId (_docId):', existingDocId);
            console.log('firestoreDocId to use:', firestoreDocId);
            console.log('dataId for id field:', dataId);

            const dataToSave = {
                id: dataId,
                name: categoryForm.name,
                description: categoryForm.description,
                order: parseInt(categoryForm.order) || 0,
                updatedAt: new Date()
            };

            if (!isEditing) {
                dataToSave.createdAt = new Date();
            }

            console.log('Data to save:', dataToSave);
            console.log('Saving to Firestore document ID:', firestoreDocId);

            await new Promise((resolve, reject) => {
                setDocument(
                    budgetDB,
                    "agenda-block-types",
                    firestoreDocId,
                    dataToSave,
                    () => resolve(),
                    (error) => reject(error)
                );
            });

            console.log('Save successful!');
            closeCategoryModal();
            setConfirmEditModal(false);
            setPendingEdit(null);
        } catch (error) {
            console.error("Error saving category:", error);
            alert("Error saving category. Check console for details.");
        } finally {
            setSaving(false);
        }
    };

    const openDeleteCategoryModal = (category) => {
        setCategoryToDelete(category);
        setDeleteCategoryModal(true);
    };

    const handleDeleteCategory = async () => {
        if (!categoryToDelete) return;

        // Use _docId (Firestore document ID) for deletion
        const docIdToDelete = categoryToDelete._docId || categoryToDelete.id;
        console.log('Deleting category with Firestore doc ID:', docIdToDelete);

        setSaving(true);
        try {
            await new Promise((resolve, reject) => {
                deleteDocument(
                    budgetDB,
                    "agenda-block-types",
                    docIdToDelete,
                    () => resolve(),
                    (error) => reject(error)
                );
            });
            setDeleteCategoryModal(false);
            setCategoryToDelete(null);
        } catch (error) {
            console.error("Error deleting category:", error);
            alert("Error deleting category. Check console for details.");
        } finally {
            setSaving(false);
        }
    };

    const confirmPendingEdit = async () => {
        console.log('=== CONFIRM PENDING EDIT ===');
        console.log('pendingEdit:', pendingEdit);
        if (!pendingEdit) return;
        await executeCategorySave();
    };

    const cancelPendingEdit = () => {
        setConfirmEditModal(false);
        setPendingEdit(null);
        // Reopen the original modal
        setCategoryModal(true);
    };

    if (loading) {
        return (
            <RebelCard>
                <div style={{ textAlign: 'center', padding: '48px' }}>
                    <RebelLoadingSpinner />
                    <p style={{ marginTop: '16px', color: '#b0b0b0' }}>Loading Configuration...</p>
                </div>
            </RebelCard>
        );
    }

    return (
        <React.Fragment>
            <RebelCard>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <RebelSectionTitle>
                        <RebelIcon icon={['fas', 'cogs']} />
                        Activity Categories
                    </RebelSectionTitle>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        {categories.length > 0 && (
                            <RebelAccentButton onClick={openAddCategoryModal}>
                                <RebelIcon icon={['fas', 'plus']} />
                                Add Category
                            </RebelAccentButton>
                        )}
                        <DevButton
                            onClick={() => setDevMode(!devMode)}
                            $active={devMode}
                        >
                            Dev
                        </DevButton>
                    </div>
                </div>
                {devMode && (
                    <DevInfoPanel>
                        <DevInfoTitle>Debug Info</DevInfoTitle>
                        <DevInfoRow>
                            <DevInfoLabel>Database:</DevInfoLabel>
                            <DevInfoValue>agenda-block-types</DevInfoValue>
                        </DevInfoRow>
                        <DevInfoRow>
                            <DevInfoLabel>Total Categories:</DevInfoLabel>
                            <DevInfoValue>{categories.length}</DevInfoValue>
                        </DevInfoRow>
                    </DevInfoPanel>
                )}

                {categories.length === 0 ? (
                    <RebelEmptyState>
                        <RebelIcon icon={['fas', 'tags']} style={{ fontSize: '3rem', marginBottom: '16px' }} />
                        <h3>No Categories Found</h3>
                        <p>No activity categories were found in the database.</p>
                        <RebelAccentButton onClick={openAddCategoryModal}>
                            <RebelIcon icon={['fas', 'plus']} />
                            Add Category
                        </RebelAccentButton>
                    </RebelEmptyState>
                ) : (
                    <CategoriesContainer>
                        {categories.map((category) => (
                            <CategoryCard key={category.id}>
                                <CategoryHeader>
                                    <CategoryName>
                                        <RebelIcon icon={['fas', 'tag']} />
                                        {category.name || category.id}
                                    </CategoryName>
                                    <CategoryActions>
                                        <ActionButton onClick={() => openEditCategoryModal(category)} title="Edit Category">
                                            <RebelIcon icon={['fas', 'edit']} />
                                        </ActionButton>
                                        <ActionButton onClick={() => openDeleteCategoryModal(category)} title="Delete Category" danger>
                                            <RebelIcon icon={['fas', 'trash']} />
                                        </ActionButton>
                                    </CategoryActions>
                                </CategoryHeader>

                                {category.description && (
                                    <CategoryDescription>{category.description}</CategoryDescription>
                                )}
                                {devMode && (
                                    <CategoryDevInfo>
                                        <DevInfoRow>
                                            <DevInfoLabel>Category ID:</DevInfoLabel>
                                            <DevInfoValue>{category.id}</DevInfoValue>
                                        </DevInfoRow>
                                        <DevInfoRow>
                                            <DevInfoLabel>Firestore Doc ID:</DevInfoLabel>
                                            <DevInfoValue>{category._docId || category.id}</DevInfoValue>
                                        </DevInfoRow>
                                        <DevInfoRow>
                                            <DevInfoLabel>Order:</DevInfoLabel>
                                            <DevInfoValue>{category.order ?? 'N/A'}</DevInfoValue>
                                        </DevInfoRow>
                                    </CategoryDevInfo>
                                )}
                            </CategoryCard>
                        ))}
                    </CategoriesContainer>
                )}
            </RebelCard>

            {/* Add/Edit Category Modal */}
            <RebelModal isOpen={categoryModal} toggle={closeCategoryModal}>
                <RebelModalHeader>
                    {editingCategory ? 'Edit Category' : 'Add New Category'}
                </RebelModalHeader>
                <RebelModalBody>
                    <FormGroup>
                        <RebelLabel>Category Name *</RebelLabel>
                        <RebelInput
                            type="text"
                            value={categoryForm.name}
                            onChange={(e) => handleCategoryFormChange('name', e.target.value)}
                            placeholder="e.g., Team Building"
                        />
                    </FormGroup>
                    <FormGroup>
                        <RebelLabel>Description</RebelLabel>
                        <RebelTextarea
                            rows="3"
                            value={categoryForm.description}
                            onChange={(e) => handleCategoryFormChange('description', e.target.value)}
                            placeholder="Brief description of this category..."
                        />
                    </FormGroup>
                    <FormGroup>
                        <RebelLabel>Display Order</RebelLabel>
                        <RebelInput
                            type="number"
                            value={categoryForm.order}
                            onChange={(e) => handleCategoryFormChange('order', e.target.value)}
                            placeholder="1"
                            min="0"
                        />
                    </FormGroup>
                </RebelModalBody>
                <RebelModalFooter>
                    <RebelButton onClick={closeCategoryModal}>
                        Cancel
                    </RebelButton>
                    <RebelAccentButton onClick={saveCategory} disabled={saving || !categoryForm.name.trim()}>
                        {saving ? 'Saving...' : editingCategory ? 'Save Changes' : 'Add Category'}
                    </RebelAccentButton>
                </RebelModalFooter>
            </RebelModal>

            {/* Delete Category Confirmation Modal */}
            <RebelModal isOpen={deleteCategoryModal} toggle={() => setDeleteCategoryModal(false)}>
                <RebelModalHeader>
                    Delete Category
                </RebelModalHeader>
                <RebelModalBody>
                    <WarningBox>
                        <RebelIcon icon={['fas', 'exclamation-circle']} />
                        <strong>Warning: This is a breaking change!</strong>
                    </WarningBox>
                    <p>Are you sure you want to delete the category:</p>
                    <HighlightText>{categoryToDelete?.name || categoryToDelete?.id}</HighlightText>
                    <p style={{ marginTop: '12px', fontSize: '13px', color: RebelColors.primaryMuted }}>
                        Existing activities using this category may be affected.
                    </p>
                </RebelModalBody>
                <RebelModalFooter>
                    <RebelButton onClick={() => setDeleteCategoryModal(false)}>
                        Cancel
                    </RebelButton>
                    <RebelDangerButton onClick={handleDeleteCategory} disabled={saving}>
                        <RebelIcon icon={['fas', 'trash']} />
                        {saving ? 'Deleting...' : 'Delete Category'}
                    </RebelDangerButton>
                </RebelModalFooter>
            </RebelModal>

            {/* Confirm Edit Modal (for breaking changes) */}
            <RebelModal isOpen={confirmEditModal} toggle={cancelPendingEdit}>
                <RebelModalHeader>
                    Confirm Changes
                </RebelModalHeader>
                <RebelModalBody>
                    <WarningBox>
                        <RebelIcon icon={['fas', 'exclamation-circle']} />
                        <strong>Warning: This is a breaking change!</strong>
                    </WarningBox>
                    <p>You are about to modify the category:</p>
                    <ChangesList>
                        {pendingEdit?.original?.name !== pendingEdit?.data?.name && (
                            <ChangeItem>
                                <span>Name:</span> {pendingEdit?.original?.name} <ArrowIcon icon={['fas', 'arrow-left']} /> {pendingEdit?.data?.name}
                            </ChangeItem>
                        )}
                        {pendingEdit?.original?.description !== pendingEdit?.data?.description && (
                            <ChangeItem>
                                <span>Description:</span> Updated
                            </ChangeItem>
                        )}
                        {pendingEdit?.original?.order !== parseInt(pendingEdit?.data?.order) && (
                            <ChangeItem>
                                <span>Order:</span> {pendingEdit?.original?.order} <ArrowIcon icon={['fas', 'arrow-left']} /> {pendingEdit?.data?.order}
                            </ChangeItem>
                        )}
                    </ChangesList>
                    <p style={{ marginTop: '16px', fontSize: '13px', color: RebelColors.primaryMuted }}>
                        Existing activities using this category may be affected.
                    </p>
                </RebelModalBody>
                <RebelModalFooter>
                    <RebelButton onClick={cancelPendingEdit}>
                        Go Back
                    </RebelButton>
                    <RebelAccentButton onClick={confirmPendingEdit} disabled={saving}>
                        {saving ? 'Saving...' : 'Confirm Changes'}
                    </RebelAccentButton>
                </RebelModalFooter>
            </RebelModal>
        </React.Fragment>
    );
};

export default RebelConfigList;

const CategoriesContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const CategoryCard = styled.div`
    background: ${RebelColors.background};
    border: 1px solid ${RebelColors.border};
    border-radius: 8px;
    padding: 20px;
    transition: all 0.3s ease;

    &:hover {
        border-color: ${RebelColors.gold}60;
        box-shadow: 0 2px 8px rgba(255, 215, 0, 0.1);
    }
`;

const CategoryHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    flex-wrap: wrap;
    gap: 12px;
`;

const CategoryName = styled.h4`
    color: ${RebelColors.text};
    font-size: 16px;
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
        color: ${RebelColors.gold};
    }
`;

const CategoryActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ActionButton = styled.button`
    background: ${props => props.danger ? `${RebelColors.danger}20` : `${RebelColors.gold}20`};
    border: 1px solid ${props => props.danger ? RebelColors.danger : RebelColors.gold};
    color: ${props => props.danger ? RebelColors.danger : RebelColors.gold};
    border-radius: 6px;
    padding: 12px 16px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        margin: 0;
        font-size: 18px;
        color: inherit;
    }

    &:hover {
        background: ${props => props.danger ? RebelColors.danger : RebelColors.gold};
        color: ${props => props.danger ? '#fff' : '#000'};
        border-color: ${props => props.danger ? RebelColors.danger : RebelColors.gold};
    }
`;

const CategoryDescription = styled.p`
    color: ${RebelColors.primaryMuted};
    font-size: 14px;
    margin: 0;
    line-height: 1.5;
`;

const FormGroup = styled.div`
    margin-bottom: 16px;

    label {
        display: block;
        margin-bottom: 8px;
    }

    input, textarea {
        width: 100%;
    }
`;

const WarningBox = styled.div`
    background: ${RebelColors.warning}15;
    border: 1px solid ${RebelColors.warning};
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${RebelColors.warning};

    svg {
        font-size: 18px;
        color: ${RebelColors.warning};
    }
`;

const HighlightText = styled.p`
    background: ${RebelColors.surfaceLight};
    padding: 10px 16px;
    border-radius: 6px;
    color: ${RebelColors.gold};
    font-weight: 600;
    margin: 12px 0;
`;

const ChangesList = styled.div`
    background: ${RebelColors.surfaceLight};
    border-radius: 6px;
    padding: 12px 16px;
    margin: 12px 0;
`;

const ChangeItem = styled.div`
    color: ${RebelColors.primary};
    font-size: 13px;
    padding: 6px 0;
    display: flex;
    align-items: center;
    gap: 8px;

    span {
        color: ${RebelColors.primaryMuted};
        min-width: 80px;
    }

    & + & {
        border-top: 1px solid ${RebelColors.border};
    }
`;

const ArrowIcon = styled(RebelIcon)`
    color: ${RebelColors.gold};
    font-size: 10px;
    transform: rotate(180deg);
`;

const DevButton = styled.button`
    background-color: ${props => props.$active ? '#4a4a4a' : 'transparent'};
    border: 1px solid #4a4a4a;
    border-radius: 4px;
    padding: 8px 12px;
    color: ${props => props.$active ? '#fff' : '#888'};
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: #4a4a4a;
        color: #fff;
    }
`;

const DevInfoPanel = styled.div`
    background-color: #1a1a2e;
    border: 1px solid #4a4a4a;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
    font-family: monospace;
`;

const DevInfoTitle = styled.div`
    color: #00d9ff;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 12px;
    text-transform: uppercase;
    letter-spacing: 1px;
`;

const DevInfoRow = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 8px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const DevInfoLabel = styled.span`
    color: #888;
    font-size: 12px;
    min-width: 120px;
`;

const DevInfoValue = styled.span`
    color: #00ff88;
    font-size: 12px;
    background-color: #0a0a15;
    padding: 4px 8px;
    border-radius: 4px;
    user-select: all;
`;

const CategoryDevInfo = styled.div`
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed #4a4a4a;
    font-family: monospace;
`;
